/**
 * Player settings and how they reach the document (task 9.8).
 *
 * Settings are per save profile, not per device: two people sharing a laptop
 * get their own theme and default difficulty along with their own progress.
 * `applySettings` is the single place that touches the document element — the
 * stylesheet reads the five attributes it writes and nothing else.
 *
 * Two of those attributes are presentation levels rather than switches, and
 * both are normalised on the way out as well as on the way in: a hand-edited or
 * downgraded save must not be able to put a value on `<html>` that no rule in
 * the stylesheet matches (`save-system` spec, "Unknown presentation values fall
 * back rather than propagate").
 */

export type ThemeSetting = 'system' | 'light' | 'dark';

/**
 * How much the game is allowed to move (design D6).
 *
 * Four values rather than three. `prefers-reduced-motion` is a vestibular
 * accessibility setting and is rightly all-or-nothing, so `system` resolves to
 * `full` or `reduced` and never to `calm` — the media query has two values and
 * inventing a third from it would be guessing on the player's behalf.
 *
 * `calm` is an explicit choice, and it exists because two different needs were
 * sharing one escape hatch. A player twenty missions in who finds celebrations
 * tiring had only `reduced`, which also removes the message arrival that makes
 * the feed readable and the harvest flight that makes acquisition legible.
 * `calm` keeps the movement that carries information and drops the rest.
 */
export type MotionSetting = 'system' | 'full' | 'calm' | 'reduced';

/** The motion level actually in force, once `system` is resolved. */
export type ResolvedMotion = 'full' | 'calm' | 'reduced';

/**
 * Whether the expressive colour layer is applied (`expressive-palette` spec).
 *
 * Independent of motion on purpose: they are different accessibility needs and
 * a player who wants one turned down should not have to turn the other down
 * with it. `plain` collapses identity hues, the outcome identities beyond the
 * semantic four, axis tinting, voice tinting and phase temperature back to the
 * semantic palette, and changes no motion behaviour at all.
 */
export type ColourSetting = 'vivid' | 'plain';

export type TextSizeSetting = 'small' | 'medium' | 'large';

/**
 * How the workspace divides vertical space between the message feed and the
 * composer dock (design D6).
 *
 * Four discrete steps rather than a pixel value: a stored pixel figure does not
 * survive the trip between a 360px phone, a rotated tablet and a desktop, and a
 * value that leaves the send control off screen on one device is the regression
 * task 19.5 was about. Each step is defined in `dvh` in `app.css`, so it keeps
 * its meaning on any viewport, and each has a name a live region can say.
 *
 * The order is the order the divider steps through: `compact` gives the
 * composer dock the most room, `maximum` gives the message feed the most.
 */
export type MessageSpaceSetting = 'compact' | 'standard' | 'roomy' | 'maximum';

/** The four sizes, smallest feed first. The divider's range, in order. */
export const MESSAGE_SPACES: readonly MessageSpaceSetting[] = [
  'compact',
  'standard',
  'roomy',
  'maximum',
];

/** What the divider and the Settings control call each size. */
export const MESSAGE_SPACE_LABELS: Readonly<Record<MessageSpaceSetting, string>> = {
  compact: 'Compact',
  standard: 'Standard',
  roomy: 'Roomy',
  maximum: 'Maximum',
};

/**
 * The composer dock's ceiling per size, in `dvh`, **at and above the layout
 * breakpoint**.
 *
 * These are the same four numbers `app.css` maps `data-message-space` to in its
 * unconditional blocks, and `settings.test.ts` reads the stylesheet and asserts
 * the two agree — so the duplication cannot rot. The stylesheet is what *paints*
 * the split; this table exists because the divider has to answer a question CSS
 * cannot be asked: which size is nearest to where the player's finger currently
 * is.
 *
 * Below the breakpoint `app.css` redefines both tokens inside a
 * `@media (width < 48rem)` block, as `min()` and `max()` expressions rather than
 * as bare `dvh` figures (design D8 of the mobile workspace redesign). This table
 * is not those numbers and must not be compared with them: the test above reads
 * the desktop blocks specifically, so a mobile ceiling can move without this
 * table being wrong, and a desktop one cannot.
 *
 * What that costs, stated rather than hidden: `nearestMessageSpace` snaps a
 * *drag* against these ceilings, so below the breakpoint the size the handle
 * lands on is the nearest desktop ceiling rather than the nearest mobile one.
 * The order is the same, so a drag still moves the split the way it is pushed;
 * the keyboard steps, which is how the ladder is actually walked in the tests,
 * are unaffected. Re-snapping the drag against a second table would mean a
 * second table, and the four names, their order and their stored values are
 * deliberately the part of this that does not vary by viewport.
 */
export const MESSAGE_SPACE_DOCK_MAX_DVH: Readonly<Record<MessageSpaceSetting, number>> = {
  compact: 80,
  standard: 70,
  roomy: 55,
  maximum: 40,
};

/**
 * The size whose dock ceiling is nearest `fraction` — the share of the viewport
 * height a drag is currently asking the composer dock to occupy, from 0 to 1.
 *
 * This is the snap. A drag is continuous and the sizes are not, so the divider
 * reports the nearest one rather than inventing a fifth. Ties go to the larger
 * ceiling, which is the smaller feed: a drag that lands exactly between two
 * sizes has not asked for the conversation to grow.
 */
export function nearestMessageSpace(fraction: number): MessageSpaceSetting {
  let nearest: MessageSpaceSetting = MESSAGE_SPACES[0] as MessageSpaceSetting;
  let best = Number.POSITIVE_INFINITY;
  for (const size of MESSAGE_SPACES) {
    const distance = Math.abs(MESSAGE_SPACE_DOCK_MAX_DVH[size] / 100 - fraction);
    if (distance < best) {
      best = distance;
      nearest = size;
    }
  }
  return nearest;
}

export interface ShellSettings {
  /** A profile id from `profiles.json`, pre-selected on the difficulty screen. */
  readonly defaultProfileId: string;
  readonly theme: ThemeSetting;
  readonly motion: MotionSetting;
  /** Whether the expressive palette is applied. See `ColourSetting`. */
  readonly colour: ColourSetting;
  readonly textSize: TextSizeSetting;
  readonly sound: boolean;
  /** How much of the workspace the message feed gets. See `MessageSpaceSetting`. */
  readonly messageSpace: MessageSpaceSetting;
}

export const DEFAULT_SETTINGS: ShellSettings = {
  defaultProfileId: 'analyst',
  theme: 'system',
  motion: 'system',
  // `vivid` is the default because this is what the expressive layer was built
  // for. `system` stays the motion default, so `prefers-reduced-motion` is
  // still honoured out of the box.
  colour: 'vivid',
  textSize: 'medium',
  sound: true,
  // `standard` reproduces the pre-existing split exactly, so nothing moves for
  // a player who never touches the divider.
  messageSpace: 'standard',
};

const TEXT_SIZE_ATTR: Record<TextSizeSetting, string> = {
  small: 's',
  medium: 'm',
  large: 'l',
};

/** True when the query matches, or `false` where `matchMedia` is unavailable. */
function prefers(query: string): boolean {
  if (typeof globalThis.matchMedia !== 'function') return false;
  return globalThis.matchMedia(query).matches;
}

/** The theme actually painted, once `system` is resolved against the device. */
export function resolveTheme(setting: ThemeSetting): 'light' | 'dark' {
  if (setting !== 'system') return setting;
  return prefers('(prefers-color-scheme: dark)') ? 'dark' : 'light';
}

/**
 * The motion level in force. `system` follows `prefers-reduced-motion`; the
 * explicit settings override it in both directions, because a player who wants
 * animation off on this one device should not have to change an OS setting.
 *
 * `system` never resolves to `calm`. See `MotionSetting`.
 */
export function resolveMotion(setting: MotionSetting): ResolvedMotion {
  if (setting !== 'system') return setting;
  return prefers('(prefers-reduced-motion: reduce)') ? 'reduced' : 'full';
}

/**
 * Writes the resolved settings onto `<html>`. Every visual consequence follows
 * from these five attributes, so a component never asks what the theme is.
 *
 * Still the only function in the application that touches the document
 * element. `data-motion-paused` is the one exception, and it belongs to the
 * motion layer rather than to a setting: it is written when the tab is hidden
 * and cleared when it comes back.
 */
export function applySettings(root: HTMLElement, settings: ShellSettings): void {
  root.dataset['theme'] = resolveTheme(settings.theme);
  root.dataset['motion'] = normaliseMotion(settings.motion);
  // Normalised on the way out for the same reason the message space is: an
  // unrecognised value must resolve to the default rather than reach the
  // document, where the stylesheet would find no rules for it at all.
  root.dataset['colour'] = normaliseColour(settings.colour);
  root.dataset['textSize'] = TEXT_SIZE_ATTR[settings.textSize];
  // Written through the same normalisation the stored value goes through, so a
  // hand-edited save cannot put an unknown size on the document element and
  // leave the dock with no ceiling at all.
  root.dataset['messageSpace'] = normaliseMessageSpace(settings.messageSpace);
}

/** A stored or supplied size, or the default where it is not one of the four. */
export function normaliseMessageSpace(value: unknown): MessageSpaceSetting {
  return MESSAGE_SPACES.includes(value as MessageSpaceSetting)
    ? (value as MessageSpaceSetting)
    : DEFAULT_SETTINGS.messageSpace;
}

/** Every motion value the application recognises, in the order Settings lists them. */
export const MOTION_SETTINGS: readonly MotionSetting[] = ['system', 'full', 'calm', 'reduced'];

/** Every colour value, in the order Settings lists them. */
export const COLOUR_SETTINGS: readonly ColourSetting[] = ['vivid', 'plain'];

/**
 * A stored motion value, resolved to the level that goes on the document.
 *
 * A hand-edited or downgraded save can hold anything. An unrecognised value
 * falls back to the *default setting* and is then resolved, rather than being
 * written through — which is what keeps `data-motion` a value the stylesheet
 * has rules for (`save-system` spec, "Unknown presentation values fall back").
 */
export function normaliseMotion(value: unknown): ResolvedMotion {
  return resolveMotion(
    MOTION_SETTINGS.includes(value as MotionSetting)
      ? (value as MotionSetting)
      : DEFAULT_SETTINGS.motion,
  );
}

/** A stored or supplied colour value, or the default where it is neither. */
export function normaliseColour(value: unknown): ColourSetting {
  return COLOUR_SETTINGS.includes(value as ColourSetting)
    ? (value as ColourSetting)
    : DEFAULT_SETTINGS.colour;
}

/** Drops unknown keys and out-of-range values from imported or migrated data. */
export function normaliseSettings(value: unknown): ShellSettings {
  const raw = (value ?? {}) as Partial<Record<keyof ShellSettings, unknown>>;
  const oneOf = <T extends string>(candidate: unknown, allowed: readonly T[], fallback: T): T =>
    allowed.includes(candidate as T) ? (candidate as T) : fallback;

  return {
    defaultProfileId:
      typeof raw.defaultProfileId === 'string' && raw.defaultProfileId.length > 0
        ? raw.defaultProfileId
        : DEFAULT_SETTINGS.defaultProfileId,
    theme: oneOf(raw.theme, ['system', 'light', 'dark'] as const, DEFAULT_SETTINGS.theme),
    motion: oneOf(raw.motion, MOTION_SETTINGS, DEFAULT_SETTINGS.motion),
    colour: normaliseColour(raw.colour),
    textSize: oneOf(raw.textSize, ['small', 'medium', 'large'] as const, DEFAULT_SETTINGS.textSize),
    sound: typeof raw.sound === 'boolean' ? raw.sound : DEFAULT_SETTINGS.sound,
    messageSpace: normaliseMessageSpace(raw.messageSpace),
  };
}
